import type { LanguageCode } from '@/types/i18n';

export type LocaleBrowserFamily = 'mobile_safari' | 'safari' | 'chromium' | 'firefox' | 'other';
export type LocaleFailureReason =
  | 'network'
  | 'http_4xx'
  | 'http_5xx'
  | 'timeout'
  | 'parse'
  | 'payload_invalid'
  | 'release_unavailable'
  | 'runtime_unavailable'
  | 'storage_unavailable'
  | 'storage_quota'
  | 'cache_corrupt'
  | 'wrong_language'
  | 'unknown';
export type LocaleDiagnosticEventType =
  | 'switch_started'
  | 'switch_success'
  | 'switch_failure'
  | 'wrong_language_invariant'
  | 'cache_hit'
  | 'cache_miss'
  | 'storage_unavailable'
  | 'storage_quota'
  | 'corrupt_cache'
  | 'fallback_activated'
  | 'retry'
  | 'cache_reset'
  | 'runtime_request_success'
  | 'runtime_request_failure'
  | 'parse_failure'
  | 'version_change'
  | 'support_snapshot_copied';

export interface LocaleDiagnosticEvent {
  schema_version: 1;
  event_type: LocaleDiagnosticEventType;
  occurred_at: string;
  browser_family: LocaleBrowserFamily;
  requested_locale?: LanguageCode | null;
  pending_locale?: LanguageCode | null;
  active_locale?: LanguageCode | null;
  release_version?: string | null;
  cache_source?: 'memory_cache' | 'session_cache' | 'network' | 'english_bundle' | 'bootstrap_bundle' | null;
  storage_status?: 'hit' | 'miss' | 'stored' | 'unavailable' | 'quota' | 'corrupt' | null;
  failure_reason?: LocaleFailureReason | null;
  correlation_id?: string | null;
  route_id?: string | null;
  fetch_ms?: number | null;
  parse_ms?: number | null;
  total_ms?: number | null;
}

type LocaleDiagnosticInput = Omit<
  LocaleDiagnosticEvent,
  'schema_version' | 'occurred_at' | 'browser_family'
>;

export interface LocaleSupportState {
  requestedLanguage: LanguageCode;
  pendingLanguage: LanguageCode | null;
  activeLanguage: LanguageCode | null;
  releaseVersion: string | null;
  cacheSource: LocaleDiagnosticEvent['cache_source'];
  degradedReason: string | null;
  isLoading: boolean;
  routeId: string | null;
}

const MAX_RECENT_EVENTS = 50;
const MAX_PENDING_EVENTS = 50;
const FLUSH_DELAY_MS = 1_500;
const counters: Record<string, number> = {};
const recentEvents: LocaleDiagnosticEvent[] = [];
let pendingEvents: LocaleDiagnosticEvent[] = [];
let telemetryEnabled = false;
let telemetrySampled = false;
let telemetryConfiguration: string | null = null;
let flushTimer: number | null = null;

export function detectLocaleBrowserFamily(): LocaleBrowserFamily {
  if (typeof navigator === 'undefined') return 'other';
  const userAgent = navigator.userAgent;
  const isWebKit = /AppleWebKit/i.test(userAgent);
  const isSafari = isWebKit && !/(CriOS|FxiOS|EdgiOS|OPiOS|Chrome|Chromium|Edg)/i.test(userAgent);
  const isTouchApple = /iPad|iPhone|iPod/i.test(userAgent)
    || (/Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1);
  if (isSafari && isTouchApple) return 'mobile_safari';
  if (isSafari) return 'safari';
  if (/Firefox|FxiOS/i.test(userAgent)) return 'firefox';
  if (/Chrome|Chromium|CriOS|Edg|EdgiOS/i.test(userAgent)) return 'chromium';
  return 'other';
}

export function configureLocaleDiagnostics(enabled: boolean, samplingRate: number): void {
  const boundedRate = Math.max(0, Math.min(1, samplingRate));
  const nextConfiguration = `${enabled}:${boundedRate}`;
  telemetryEnabled = enabled;
  if (nextConfiguration !== telemetryConfiguration) {
    telemetryConfiguration = nextConfiguration;
    telemetrySampled = enabled && (boundedRate >= 1 || Math.random() < boundedRate);
  }
}

function safeDuration(value: number | null | undefined): number | undefined {
  if (value === null || value === undefined || !Number.isFinite(value)) return undefined;
  return Math.max(0, Math.min(120_000, Math.round(value)));
}

function cleanEvent(event: LocaleDiagnosticEvent): LocaleDiagnosticEvent {
  return Object.fromEntries(
    Object.entries({
      ...event,
      fetch_ms: safeDuration(event.fetch_ms),
      parse_ms: safeDuration(event.parse_ms),
      total_ms: safeDuration(event.total_ms),
    }).filter(([, value]) => value !== null && value !== undefined),
  ) as unknown as LocaleDiagnosticEvent;
}

async function flushLocaleDiagnostics(): Promise<void> {
  flushTimer = null;
  if (!telemetryEnabled || !telemetrySampled || pendingEvents.length === 0) return;
  const events = pendingEvents.slice(0, MAX_PENDING_EVENTS);
  pendingEvents = pendingEvents.slice(MAX_PENDING_EVENTS);
  try {
    await fetch('/api/localization/runtime/diagnostics/events', {
      method: 'POST',
      cache: 'no-store',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ events }),
    });
  } catch {
    // Diagnostics must never affect locale activation or recursively report itself.
  }
  if (pendingEvents.length > 0) scheduleFlush();
}

function scheduleFlush(): void {
  if (flushTimer !== null || typeof window === 'undefined') return;
  flushTimer = window.setTimeout(() => {
    void flushLocaleDiagnostics();
  }, FLUSH_DELAY_MS);
}

export function recordLocaleDiagnostic(input: LocaleDiagnosticInput): void {
  const event = cleanEvent({
    schema_version: 1,
    occurred_at: new Date().toISOString(),
    browser_family: detectLocaleBrowserFamily(),
    ...input,
  });
  counters[event.event_type] = (counters[event.event_type] ?? 0) + 1;
  recentEvents.unshift(event);
  recentEvents.splice(MAX_RECENT_EVENTS);
  if (telemetryEnabled && telemetrySampled) {
    pendingEvents.push(event);
    if (pendingEvents.length >= 10) void flushLocaleDiagnostics();
    else scheduleFlush();
  }
}

export function makeLocaleCorrelationId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `locale-${Date.now().toString(36)}-${Math.random().toString(16).slice(2, 10)}`;
  }
}

export function normalizeLocaleFailureReason(error: unknown): LocaleFailureReason {
  if (error instanceof LocaleRuntimeRequestError) return error.reason;
  if (typeof DOMException !== 'undefined'
    && error instanceof DOMException
    && error.name === 'TimeoutError') return 'timeout';
  return 'unknown';
}

export class LocaleRuntimeRequestError extends Error {
  readonly reason: LocaleFailureReason;
  readonly correlationId: string | null;

  constructor(
    message: string,
    reason: LocaleFailureReason,
    correlationId: string | null = null,
  ) {
    super(message);
    this.name = 'LocaleRuntimeRequestError';
    this.reason = reason;
    this.correlationId = correlationId;
  }
}

export function getLocaleSupportSnapshot(state: LocaleSupportState): Record<string, unknown> {
  return {
    schema_version: 1,
    created_at: new Date().toISOString(),
    browser_family: detectLocaleBrowserFamily(),
    locale_state: {
      requestedLanguage: state.requestedLanguage,
      pendingLanguage: state.pendingLanguage,
      activeLanguage: state.activeLanguage,
      releaseVersion: state.releaseVersion,
      cacheSource: state.cacheSource,
      degradedReason: state.degradedReason,
      isLoading: state.isLoading,
      routeId: state.routeId,
    },
    counters: { ...counters },
    recent_events: recentEvents.slice(0, 20),
  };
}

export function resetLocaleDiagnosticsForTests(): void {
  for (const key of Object.keys(counters)) delete counters[key];
  recentEvents.length = 0;
  pendingEvents = [];
  if (flushTimer !== null && typeof window !== 'undefined') {
    window.clearTimeout(flushTimer);
  }
  flushTimer = null;
  telemetryEnabled = false;
  telemetrySampled = false;
  telemetryConfiguration = null;
}
